import { useMemo, useState } from "react";
import { IconCheck, IconSparkles, IconX } from "@tabler/icons-react";
import { Button } from "../../../../components/common/button";
import { CheckboxField } from "../../../../components/common/checkbox-field";
import { Heading, Text } from "../../../../components/common/typography";
import type { DatasheetAiSummary } from "../../utils/datasheet-extraction-schema";

export interface OptionForMatching {
  value: string;
  label: string;
}

export interface AutofillPatch {
  name?: string;
  description?: string;
  sku?: string;
  brandId?: string;
  appendedNotes?: string;
}

interface Props {
  open: boolean;
  extraction: DatasheetAiSummary | null;
  brandOptions: OptionForMatching[];
  categoryOptions: OptionForMatching[];
  onApply: (patch: AutofillPatch) => void;
  onSkip: () => void;
}

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

function matchOption(target: string | undefined, options: OptionForMatching[]): OptionForMatching | null {
  if (!target) return null;
  const needle = normalize(target);
  if (!needle) return null;
  const exact = options.find((o) => normalize(o.label) === needle);
  if (exact) return exact;
  const startsWith = options.find((o) => normalize(o.label).startsWith(needle) || needle.startsWith(normalize(o.label)));
  return startsWith ?? null;
}

function buildOverflowNotes(extraction: DatasheetAiSummary): string {
  const lines: string[] = [];
  if (extraction.dimensions) lines.push(`Dimensions: ${extraction.dimensions}`);
  if (extraction.power) lines.push(`Power: ${extraction.power}`);
  if (extraction.operating_conditions) lines.push(`Operating conditions: ${extraction.operating_conditions}`);
  if (extraction.warranty) lines.push(`Warranty: ${extraction.warranty}`);
  if (extraction.certifications?.length) lines.push(`Certifications: ${extraction.certifications.join(", ")}`);
  if (extraction.connectivity?.length) lines.push(`Connectivity: ${extraction.connectivity.join(", ")}`);
  if (extraction.in_the_box?.length) lines.push(`In the box: ${extraction.in_the_box.join(", ")}`);
  if (extraction.key_features?.length) {
    lines.push("Key features:");
    for (const f of extraction.key_features) lines.push(`  - ${f}`);
  }
  return lines.join("\n");
}

export function DatasheetExtractionDialog({
  open,
  extraction,
  brandOptions,
  categoryOptions,
  onApply,
  onSkip,
}: Props) {
  const candidateName = extraction?.title?.trim() ?? "";
  const candidateDescription = extraction?.summary?.trim() ?? "";
  const candidateSku = extraction?.model_numbers?.[0]?.trim() ?? "";
  const candidateBrandRaw = extraction?.manufacturer?.trim() ?? "";

  const brandMatch = useMemo(
    () => matchOption(candidateBrandRaw, brandOptions),
    [candidateBrandRaw, brandOptions],
  );
  const overflowNotes = useMemo(() => (extraction ? buildOverflowNotes(extraction) : ""), [extraction]);

  // Default-on for everything that has a value; brand only when matched.
  const [pickName, setPickName] = useState(Boolean(candidateName));
  const [pickDescription, setPickDescription] = useState(Boolean(candidateDescription));
  const [pickSku, setPickSku] = useState(Boolean(candidateSku));
  const [pickBrand, setPickBrand] = useState(Boolean(brandMatch));
  const [pickNotes, setPickNotes] = useState(false);

  if (!open || !extraction) return null;

  const handleApply = () => {
    const patch: AutofillPatch = {};
    if (pickName && candidateName) patch.name = candidateName;
    if (pickDescription && candidateDescription) patch.description = candidateDescription;
    if (pickSku && candidateSku) patch.sku = candidateSku;
    if (pickBrand && brandMatch) patch.brandId = brandMatch.value;
    if (pickNotes && overflowNotes) patch.appendedNotes = overflowNotes;
    onApply(patch);
  };

  // Anything to show at all? If extraction is empty, render a tiny "nothing extracted" state.
  const hasAnyContent =
    candidateName || candidateDescription || candidateSku || candidateBrandRaw || overflowNotes;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onSkip}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col gap-5 overflow-hidden rounded-2xl bg-bgWhite p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <IconSparkles size={24} className="shrink-0 text-brandPrimary" />
            <Heading variant="h3" tone="dark">
              Datasheet extracted
            </Heading>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onSkip}
            className="inline-flex size-9 items-center justify-center rounded-full text-textBody hover:bg-bgLight"
          >
            <IconX size={20} />
          </button>
        </div>

        {!hasAnyContent ? (
          <Text variant="body" tone="muted">
            We couldn't pull any reusable fields out of this PDF. The full datasheet will still be
            attached to the product and fed into the AI assistant.
          </Text>
        ) : (
          <>
            <Text variant="body" tone="muted">
              We read the PDF and pulled out the fields below. Tick the ones you want to autofill,
              review on the form, then submit.
            </Text>

            <div className="flex flex-col gap-3 overflow-y-auto pr-1">
              {candidateName ? (
                <ExtractionRow
                  checked={pickName}
                  onChange={setPickName}
                  label="Product name"
                  value={candidateName}
                />
              ) : null}

              {candidateDescription ? (
                <ExtractionRow
                  checked={pickDescription}
                  onChange={setPickDescription}
                  label="Description"
                  value={candidateDescription}
                />
              ) : null}

              {candidateSku ? (
                <ExtractionRow
                  checked={pickSku}
                  onChange={setPickSku}
                  label="SKU"
                  value={candidateSku}
                />
              ) : null}

              {candidateBrandRaw ? (
                brandMatch ? (
                  <ExtractionRow
                    checked={pickBrand}
                    onChange={setPickBrand}
                    label="Brand"
                    value={`${brandMatch.label} (matched from "${candidateBrandRaw}")`}
                  />
                ) : (
                  <UnmatchedRow
                    label="Brand"
                    value={candidateBrandRaw}
                    hint="No matching brand found — pick one manually after closing."
                  />
                )
              ) : null}

              {/* Category isn't in the schema — show category options count as a hint if there's
                  a manufacturer/title pattern that resembles one. Keep it simple: skip auto-match. */}
              {categoryOptions.length === 0 ? null : null}

              {overflowNotes ? (
                <ExtractionRow
                  checked={pickNotes}
                  onChange={setPickNotes}
                  label="Append details to Internal notes"
                  value={overflowNotes}
                  multiline
                />
              ) : null}
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-borderDefault pt-4">
          <Button variant="ghost" size="md" onClick={onSkip}>
            Skip
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleApply}
            leftIcon={<IconCheck size={18} />}
            disabled={!hasAnyContent}
          >
            Apply selected
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ExtractionRowProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  value: string;
  multiline?: boolean;
}

function ExtractionRow({ checked, onChange, label, value, multiline }: ExtractionRowProps) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-borderDefault bg-bgWhite p-3">
      <div className="pt-0.5">
        <CheckboxField variant="inline" checked={checked} onChange={onChange} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Text variant="bodySmBold" tone="dark">
          {label}
        </Text>
        {multiline ? (
          <pre className="m-0 whitespace-pre-wrap break-words font-sans text-sm text-textBody">
            {value}
          </pre>
        ) : (
          <Text variant="body" tone="body" className="break-words">
            {value}
          </Text>
        )}
      </div>
    </div>
  );
}

interface UnmatchedRowProps {
  label: string;
  value: string;
  hint: string;
}

function UnmatchedRow({ label, value, hint }: UnmatchedRowProps) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-dashed border-borderDefault bg-bgLight p-3">
      <Text variant="bodySmBold" tone="dark">
        {label}: {value}
      </Text>
      <Text variant="bodyXs" tone="muted">
        {hint}
      </Text>
    </div>
  );
}
